
import { Page } from '@/types/crawler';
import { DeepAnalysisResult, ExpertInsight } from '@/types/analysisTypes';
import { analyzeContent } from '../deep-content-analysis';
import { processAnalysisResult } from './resultProcessingService';
import { UniversalCrawlerService } from '../universalCrawlerService';

/**
 * Analyze a page using OpenAI
 * @param apiKey OpenAI API key
 * @param model Model to use
 * @param page Page to analyze
 * @returns Analysis result
 */
export const analyzePageWithOpenAI = async (
  apiKey: string,
  model: string,
  page: Page
): Promise<DeepAnalysisResult> => {
  console.log('[DEBUG] analyzePageWithOpenAI - START for URL:', page.url);
  
  let contentToAnalyze = page.content;
  
  // If no content is available, fetch it on-demand
  if (!contentToAnalyze || contentToAnalyze.trim().length === 0) {
    console.log(`No content found for ${page.url}, fetching content...`);
    
    try {
      const contentResult = await UniversalCrawlerService.fetchContent(page.url);
      
      if (!contentResult.success || !contentResult.content) {
        return createEmptyAnalysisResult(`Failed to fetch content: ${contentResult.error || 'No content available'}`);
      }
      
      contentToAnalyze = contentResult.content;
      console.log(`Successfully fetched ${contentToAnalyze.length} characters for ${page.url}`);
    } catch (error) {
      console.error(`Error fetching content for ${page.url}:`, error);
      return createErrorAnalysisResult(error as Error);
    }
  }
  
  // Use the page URL to enable caching
  try {
    console.log('[DEBUG] analyzePageWithOpenAI - Calling analyzeContent...');
    const result = await analyzeContent(contentToAnalyze, page.url);
    
    console.log('[DEBUG] analyzePageWithOpenAI - analyzeContent returned:', {
      ai_optimization_present: 'ai_optimization_score' in (result.category_scores || {}),
      keys: Object.keys(result.category_scores || {}),
      overall_score: result.overall_score
    });
    
    // Log analysis result
    console.log(`Analysis result for ${page.url}: score ${result.overall_score}`);

    console.log('[DEBUG] analyzePageWithOpenAI - Calling processAnalysisResult...');
    // Process and standardize the analysis result
    const processedResult = processAnalysisResult(result);
    
    console.log('[DEBUG] analyzePageWithOpenAI - processAnalysisResult returned:', {
      ai_optimization_present: 'ai_optimization_score' in (processedResult.category_scores || {}),
      keys: Object.keys(processedResult.category_scores || {}),
      overall_score: processedResult.overall_score
    });
    
    return processedResult;
  } catch (error) {
    console.error(`Error analyzing page ${page.url}:`, error);
    return createErrorAnalysisResult(error as Error);
  }
};

/**
 * Create an empty analysis result with default values
 */
function createEmptyAnalysisResult(errorMessage: string): DeepAnalysisResult {
  return {
    error: errorMessage,
    overall_score: 0,
    category_scores: {
      content_quality: 0,
      EEAT: 0,
      helpfulness: 0,
      shareability: 0,
      ai_optimization_score: 0
    },
    actionable_fixes: [],
    shareability_scores: {
      beginner: { score: 0, explanation: '', improvement: '' },
      intermediate: { score: 0, explanation: '', improvement: '' },
      expert: { score: 0, explanation: '', improvement: '' }
    },
    bonus_section: {
      expert_insight: { 
        title: 'Expert Insight',
        intro: 'No content available for analysis',
        key_takeaways: [],
        conclusion: ''
      },
      unexpected_analogy: {
        title: 'Unexpected Analogy',
        intro: '',
        key_points: [],
        conclusion: ''
      },
      challenged_assumption: {
        title: 'Challenged Assumption',
        intro: '',
        key_reasons: [],
        solution: ''
      },
      insider_tip: {
        title: 'Insider Tip',
        intro: '',
        step_by_step: [],
        final_takeaway: ''
      }
    }
  };
}

/**
 * Create an error analysis result
 */
function createErrorAnalysisResult(error: Error): DeepAnalysisResult {
  // For error expert insight, use proper type casting
  const expertErrorInsight: ExpertInsight = { 
    title: 'Error Occurred',
    intro: `Analysis error: ${error.message || 'Unknown error'}`,
    key_takeaways: ['The analysis encountered an error', 'Try again later', 'Check your API key settings'],
    conclusion: 'Contact support if this issue persists'
  };
  
  return {
    error: error.message || 'Unknown error during analysis',
    overall_score: 0,
    category_scores: {
      content_quality: 0,
      EEAT: 0,
      helpfulness: 0,
      shareability: 0,
      ai_optimization_score: 0
    },
    actionable_fixes: [],
    shareability_scores: {
      beginner: { score: 0, explanation: '', improvement: '' },
      intermediate: { score: 0, explanation: '', improvement: '' },
      expert: { score: 0, explanation: '', improvement: '' }
    },
    bonus_section: {
      expert_insight: expertErrorInsight,
      unexpected_analogy: {
        title: '',
        intro: '',
        key_points: [],
        conclusion: ''
      },
      challenged_assumption: {
        title: '',
        intro: '',
        key_reasons: [],
        solution: ''
      },
      insider_tip: {
        title: '',
        intro: '',
        step_by_step: [],
        final_takeaway: ''
      }
    }
  };
}
